import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Strikethrough, Type, Palette, Eraser } from "lucide-react";

const SIZES = [
  { label: "Small", value: "1" },
  { label: "Default", value: "3" },
  { label: "Large", value: "5" },
  { label: "X-Large", value: "6" },
  { label: "Huge", value: "7" },
];
const COLORS = ["#18181B", "#71717A", "#F74B64", "#FFA62B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

const SANITIZE_OPTS = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "s", "strike", "br", "span", "font", "a", "div", "p"],
  ALLOWED_ATTR: ["style", "href", "target", "rel", "color", "size"],
  ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:|\/)/i,
};

export function sanitizeHTML(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, SANITIZE_OPTS);
}

/**
 * Lightweight rich-text editor with toolbar (Bold / Italic / Underline / Strike / Size / Color).
 * Stores HTML. Single-line variant via `singleLine` prop.
 */
export default function RichTextEditor({ value, onChange, singleLine = false, testid = "rte", placeholder = "" }) {
  const ref = useRef(null);

  // Initialise content on mount and when value resets externally (avoid clobbering during typing)
  useEffect(() => {
    if (!ref.current) return;
    const current = ref.current.innerHTML;
    if (value !== current) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    // After exec, propagate the change
    if (ref.current && onChange) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };

  const onInput = () => {
    if (ref.current && onChange) onChange(ref.current.innerHTML);
  };

  const onKeyDown = (e) => {
    if (singleLine && e.key === "Enter") {
      e.preventDefault();
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text");
    document.execCommand("insertText", false, text);
  };

  return (
    <div className="rte-wrapper border border-zinc-200 rounded-lg overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-zinc-200 bg-zinc-50">
        <Btn label="Bold" onClick={() => exec("bold")} testid={`${testid}-bold`}><Bold size={12} /></Btn>
        <Btn label="Italic" onClick={() => exec("italic")} testid={`${testid}-italic`}><Italic size={12} /></Btn>
        <Btn label="Underline" onClick={() => exec("underline")} testid={`${testid}-underline`}><Underline size={12} /></Btn>
        <Btn label="Strike" onClick={() => exec("strikeThrough")} testid={`${testid}-strike`}><Strikethrough size={12} /></Btn>

        <span className="w-px h-4 bg-zinc-200 mx-1" />

        <select
          onChange={(e) => exec("fontSize", e.target.value)}
          defaultValue="3"
          title="Size"
          className="text-[11px] border border-zinc-200 rounded px-1 py-0.5 bg-white"
          data-testid={`${testid}-size`}
        >
          {SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        <span className="w-px h-4 bg-zinc-200 mx-1" />

        <div className="flex items-center gap-1">
          <Palette size={11} className="text-zinc-500" />
          {COLORS.map((c) => (
            <button key={c} type="button" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => exec("foreColor", c)} title={c}
                    className="w-4 h-4 rounded-full border border-zinc-200" style={{ background: c }}
                    data-testid={`${testid}-color-${c}`} />
          ))}
        </div>

        <span className="w-px h-4 bg-zinc-200 mx-1" />
        <Btn label="Clear formatting" onClick={() => exec("removeFormat")} testid={`${testid}-clear`}>
          <Eraser size={12} />
        </Btn>
      </div>

      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        role="textbox"
        aria-multiline={!singleLine}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        data-testid={`${testid}-input`}
        data-placeholder={placeholder}
        className={`rte-content px-3 py-2 text-sm focus:outline-none ${singleLine ? "min-h-[2.25rem]" : "min-h-[5rem]"}`}
        style={{ wordBreak: "break-word" }}
      />
      <style>{`
        .rte-content:empty:before {
          content: attr(data-placeholder);
          color: #a1a1aa;
        }
      `}</style>
    </div>
  );
}

function Btn({ children, onClick, label, testid }) {
  return (
    <button
      type="button"
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="p-1.5 rounded hover:bg-white text-zinc-700 hover:text-zinc-900"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
